import { Intent } from "@blueprintjs/core";
import { addMinutes, format, parseJSON } from "date-fns";
import {
  ButtonContainer,
  DataTable,
  FGListen,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  IDataTableColumn,
  IFGContext,
  showError,
  showSuccess,
  useGridState
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import { AnneeFormationSuivieDocumentEditDialog, AnneeFormationSuivieDocumentUploadDialog } from ".";
import {
  AcquisScolaireHopeApi,
  AnneeFormationSuivieEditDto,
  AnneeFormationSuivieEditDtoFromJSON,
  AnneeFormationSuivieFichierGridDto
} from "../../../../../../api";
import {
  AddButton,
  DeleteButton,
  DownloadButton,
  EditButton,
  FGWalterSelectInput,
  SaveButton,
  SmallFormGenerator
} from "../../../../../../components";
import { useDialog } from "../../../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";
import { exportFile } from "../../../../../../utils";

export interface IAnneeFormationSuivieDetailProps {
  onTypeAcquisChange?: (typeAcquis: string) => void;
  idpersonne: number;
  listRoute: string;
}

export const AnneeFormationSuivieDetail: React.FunctionComponent<IAnneeFormationSuivieDetailProps> = ({
  onTypeAcquisChange,
  idpersonne,
  listRoute
}) => {
  const { t } = useTl();
  const api = useApiService(AcquisScolaireHopeApi);
  const [typeAcquis, taLoading] = useReferential(a => a.referentialGetAcquisScolaireTypes(), false, []);
  const [niveauFormations, nfLoading] = useReferential(a => a.referentialGetNiveauFormations(), true, []);
  const [anneeFormations, afLoading] = useReferential(a => a.referentialGetAnneeFormations(), false, []);
  const [ecoles, eLoading] = useReferential(a => a.referentialGetEcole(), true, []);
  const [formeFormations, ffLoading] = useReferential(a => a.referentialGetFormeFormations(), true, []);
  const [resultatDossierAdmissions, rdaLoading] = useReferential(a => a.referentialGetResultatFormations(), true, []);
  const [anneeScolaires, asLoading] = useReferential(a => a.referentialGetAnneeScolaire(), false, []);
  const history = useHistory();
  const { state, acquisId } = useParams<{
    id: string;
    tab: string;
    state: string;
    acquisId: string;
  }>();
  const editMode = React.useMemo(() => state === "edit", [state]);

  const saveFn = React.useCallback(
    d => api.acquisScolaireHopeSaveAnneeFormationSuivie({ AnneeFormationSuivieEditDto: d }),
    [api]
  );

  const { data, loading, saveItem, saving, deleteItem, deleting, validationErrors, refresh: refreshData } = useCrudApi<
    AnneeFormationSuivieEditDto
  >(
    React.useMemo(
      () => ({
        getApiFn: async () => {
          return +acquisId <= 0
            ? AnneeFormationSuivieEditDtoFromJSON({
                idanneeFormationSuivie: +acquisId,
                idpersonne: idpersonne,
                typeAcquis: "AnneeFormationSuivie"
              })
            : api.acquisScolaireHopeGetAnneeFormationSuivie({ id: +acquisId });
        },
        saveApiFn: saveFn,
        onSavedRoute: d => listRoute,
        getDeps: [acquisId],
        deleteApiFn: d => api.acquisScolaireHopeDeleteAnneeFormationSuivie({ id: d.idanneeFormationSuivie }),
        onDeletedRoute: () => listRoute
      }),
      [saveFn, acquisId, idpersonne, api, listRoute]
    )
  );
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      typeAcquis: Yup.string().required(t(ETLCodes.Required))
    });
  }, [t]);

  const additionalRightButtons = React.useCallback(
    (ctx: IFGContext<any>) =>
      +acquisId <= 0 && (
        <SaveButton
          onClick={async () => {
            await saveFn(ctx.formik.values);
            showSuccess(t(ETLCodes.SaveSuccess));
            ctx.formik.resetForm();
          }}
          text={t(ETLCodes.SauverEtNouveau)}
          minimal={false}
          loading={saving}
          disabled={!ctx.formik.dirty}
        />
      ),
    [acquisId, saveFn, saving, t]
  );

  const tableState = useGridState<any>({
    serverMode: false,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { uploadDate: "DESC", fileName: "ASC" }
  });

  const { setData } = tableState;

  React.useEffect(() => {
    if (data?.fichiers) {
      setData(data.fichiers);
    }
  }, [data?.fichiers, setData]);

  const [currentAnneeFormationSuivieFichierId, setCurrentAnneeFormationSuivieFichierId] = React.useState(null);
  const [uploadDialoOpen, setUploadDialoOpen] = React.useState(false);
  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idanneeFormationSuivieFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idanneeFormationSuivieFichier);
          await api.acquisScolaireHopeDeleteAnneeFormationSuivieFichier({ id: idanneeFormationSuivieFichier });
          refreshData();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, refreshData, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idanneeFormationSuivieFichier: number) => {
      try {
        setRowLoading(idanneeFormationSuivieFichier);
        const file = await api.acquisScolaireHopeDownloadAnneeFormationSuivieFichier({
          id: idanneeFormationSuivieFichier
        });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: AnneeFormationSuivieFichierGridDto) => (
          <ButtonContainer>
            <EditButton
              minimal={true}
              onClick={() => setCurrentAnneeFormationSuivieFichierId(row.idanneeFormationSuivieFichier)}
              loading={rowLoading === row.idanneeFormationSuivieFichier}
              disabled={!!rowLoading && rowLoading !== row.idanneeFormationSuivieFichier}
            />
            <DownloadButton
              minimal={true}
              onClick={() => downloadFichier(row.idanneeFormationSuivieFichier)}
              loading={rowLoading === row.idanneeFormationSuivieFichier}
              disabled={!!rowLoading && rowLoading !== row.idanneeFormationSuivieFichier}
            />
            <DeleteButton
              minimal={true}
              onDelete={() => deleteFichier(row.idanneeFormationSuivieFichier)}
              loading={rowLoading === row.idanneeFormationSuivieFichier}
              disabled={!!rowLoading && rowLoading !== row.idanneeFormationSuivieFichier}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.NomFichier),
        fieldName: "fileName"
      },
      {
        header: () => t(ETLCodes.DateUpload),
        fieldName: "uploadDate"
      },
      {
        header: () => t(ETLCodes.UtilisateurUpload),
        fieldName: "creationUserName"
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "typeAcquisFichier"
      }
    ],
    [deleteFichier, downloadFichier, rowLoading, t]
  );

  const formatDate = React.useCallback((date: Date) => {
    return format(parseJSON(addMinutes(date, date.getTimezoneOffset())), "dd/MM/yyyy HH:mm");
  }, []);

  const onClose = React.useCallback(
    (refresh?: boolean) => {
      setCurrentAnneeFormationSuivieFichierId(null);
      setUploadDialoOpen(false);
      if (refresh) {
        refreshData();
      }
    },
    [refreshData]
  );

  const onAdd = React.useCallback(() => {
    setUploadDialoOpen(true);
  }, []);

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      showColons
      editMode={editMode}
      minLabelWidth={180}
      labelAlignment="right"
      formatDate="dd/MM/yyyy"
      validationSchema={FormSchema}
      loading={loading}
      onCancel={() => history.push(listRoute)}
      saving={saving}
      showDeleteButton={+acquisId > 0}
      onDelete={deleteItem}
      deleting={deleting}
      validationErrors={validationErrors}
      additionalRightButtons={additionalRightButtons}
    >
      <FieldGroup columns={2}>
        <FieldGroup
          fieldsetProps={{
            title: t(ETLCodes.AnneeFormationSuivie)
          }}
        >
          <FGListen field="typeAcquis" onChanged={onTypeAcquisChange}></FGListen>
          <FGWalterSelectInput
            label={t(ETLCodes.Type)}
            name="typeAcquis"
            items={typeAcquis}
            loading={taLoading}
            readonly={+(acquisId ?? 0) > 0}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.AnneeFormation)}
            name="idanneeDeFormation"
            items={anneeScolaires}
            loading={asLoading}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.Niveau)}
            name="idniveau"
            items={niveauFormations}
            loading={nfLoading}
          />
          <FGWalterSelectInput label={t(ETLCodes.Annee)} name="idannee" items={anneeFormations} loading={afLoading} />
          <FGWalterSelectInput label={t(ETLCodes.Forme)} name="idforme" items={formeFormations} loading={ffLoading} />
          <FGTextInput label={t(ETLCodes.Option)} name="optionFormation" />
          <FGWalterSelectInput label={t(ETLCodes.Ecoles)} name="idecole" items={ecoles} loading={eLoading} />
          <FGWalterSelectInput
            label={t(ETLCodes.Resultat)}
            name="idresultat"
            items={resultatDossierAdmissions}
            loading={rdaLoading}
          />
          <FGTextAreaInput name="remarque" label={t(ETLCodes.Remarque)} />
        </FieldGroup>
      </FieldGroup>
      <FieldGroup
        visible={+acquisId > 0}
        fieldsetProps={{
          title: t(ETLCodes.Documents),
          rightElement: (
            <AddButton
              onClick={e => {
                e.stopPropagation();
                onAdd();
              }}
              text={t(ETLCodes.General_Add)}
              intent={Intent.PRIMARY}
            />
          )
        }}
      >
        <DataTable
          dateFormat="dd/MM/yyyy HH:mm"
          formatDate={formatDate}
          tableState={tableState}
          loading={loading}
          columns={columns}
        ></DataTable>
        {uploadDialoOpen && (
          <AnneeFormationSuivieDocumentUploadDialog
            onClose={onClose}
            dialogOpen={uploadDialoOpen}
            idanneeFormationSuivie={+acquisId}
          />
        )}
        {!!currentAnneeFormationSuivieFichierId && (
          <AnneeFormationSuivieDocumentEditDialog
            onClose={onClose}
            idanneeFormationSuivieFichier={currentAnneeFormationSuivieFichierId}
          />
        )}
      </FieldGroup>
    </SmallFormGenerator>
  );
};
